import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { messages as messagesAPI } from '../../services/api';
import ChatView from './ChatView';
import './MessagesPage.css';

function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedUserId = searchParams.get('userId');

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Pre-select conversation if userId provided in URL
  useEffect(() => {
    if (preSelectedUserId && conversations.length > 0) {
      const conversation = conversations.find(
        (c) => c.otherUserId === preSelectedUserId
      );
      if (conversation) {
        setSelectedConversation(conversation);
      } else {
        // Create new conversation for this user
        setSelectedConversation({
          otherUserId: preSelectedUserId,
          otherUserName: 'Loading...',
          messages: [],
          unreadCount: 0,
        });
      }
    }
  }, [preSelectedUserId, conversations]);

  const fetchConversations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data.conversations || []);
      setTotalUnreadCount(response.data.totalUnreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    fetchConversations(); // Refresh conversation list
  };

  const handleMessageSent = () => {
    // Refresh conversations to update last message
    fetchConversations();
  };

  const formatLastMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="messages-page">
      <div className="messages-container">
        {/* Conversation List - Desktop always visible, mobile only when no selection */}
        <div
          className={`conversation-list ${
            selectedConversation ? 'mobile-hidden' : ''
          }`}
        >
          <div className="conversation-list-header">
            <button className="back-button" onClick={() => navigate('/home')}>
              ← Back
            </button>
            <h1>Messages</h1>
            {totalUnreadCount > 0 && (
              <div className="unread-badge-large">{totalUnreadCount}</div>
            )}
          </div>

          {loading && (
            <div className="loading-state">
              <p>Loading conversations...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && conversations.length === 0 && (
            <div className="empty-state">
              <h3>No messages yet</h3>
              <p>Find a teacher and start a conversation!</p>
              <button
                className="btn-primary"
                onClick={() => navigate('/find-teachers')}
              >
                Find Teachers
              </button>
            </div>
          )}

          {!loading && !error && conversations.length > 0 && (
            <div className="conversations">
              {conversations.map((conversation) => (
                <div
                  key={conversation.otherUserId}
                  className={`conversation-item ${
                    selectedConversation?.otherUserId ===
                    conversation.otherUserId
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className="conversation-avatar">
                    {conversation.otherUserName.charAt(0).toUpperCase()}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header-row">
                      <h3 className="conversation-name">
                        {conversation.otherUserName}
                      </h3>
                      <span className="conversation-time">
                        {formatLastMessageTime(conversation.lastMessageTime)}
                      </span>
                    </div>
                    <div className="conversation-last-message">
                      <span className="message-from">
                        {conversation.lastMessageFrom === 'me' ? 'You: ' : ''}
                      </span>
                      <span className="message-text">
                        {conversation.lastMessage.length > 50
                          ? conversation.lastMessage.substring(0, 50) + '...'
                          : conversation.lastMessage}
                      </span>
                    </div>
                  </div>
                  {conversation.unreadCount > 0 && (
                    <div className="unread-badge">
                      {conversation.unreadCount}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat View - Desktop always visible if selected, mobile only when selected */}
        <div
          className={`chat-view-container ${
            !selectedConversation ? 'mobile-hidden' : ''
          }`}
        >
          {selectedConversation ? (
            <ChatView
              conversation={selectedConversation}
              onBack={handleBackToList}
              onMessageSent={handleMessageSent}
            />
          ) : (
            <div className="chat-placeholder">
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the list to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessagesPage;
